/* CustomFieldsConstants.js */

"use strict";
const EnumTypeEditor = require("./cellEditors/EnumType");
const NumberTypeEditor = require("./cellEditors/NumberType");
const StringTypeEditor = require("./cellEditors/StringType");

/**
 * @typedef {Object<string, *>} CustomFieldData - User editable fields of Custom field
 * @property {string}                           name
 * @property {string}                           dataType
 * @property {Object}                           typeInfo    - CF type information -- additional options for dataType
 * @property {?string|string[]|number|boolean}  default     - default value for the custom field
 * @property {string}                           description
 * @property {boolean}                          enabled
 * @property {boolean}                          groupable
 * @property {boolean}                          required
 * @property {boolean}                          shared
 *
 *
 * @typedef {Object<string, *>} CustomFieldTypeData
 * @property {string}                           label       - custom field type user friendly label
 * @property {string}                           dataType    - custom field data type name. to be stored in database
 * @property {?string|string[]|number|boolean}  default     - default value for the custom field
 * @property {Object}                           typeInfo    - Custom Field type information -- additional options for dataType
 * @property {Class}                            editor      - typeInfo editor class
 * @property {Function}                         validation  - value validation function
 */

/**
 * @type {CustomFieldTypeData[]}
 */
const typesData = [
    {
        label: "String",
        dataType: "String",
        default: "",
        typeInfo: {
            String: {
                /* "pattern": String(RegExp) */
            },
        },
        editor: StringTypeEditor,
        validation: stringTypeValidation,
    },
    {
        label: "Boolean",
        dataType: "Boolean",
        default: false,
        typeInfo: {
            Boolean: {},
        },
        validation: booleanTypeValidation,
    },
    {
        label: "Number",
        dataType: "Number",
        default: null,
        typeInfo: {
            Number: {
                /* "min": Number, "max": Number, "onlyInteger": Boolean } */
            },
        },
        editor: NumberTypeEditor,
        validation: numberTypeValidation,
    },
    {
        label: "Dropdown",
        dataType: "Enum",
        default: null, //[] if multiSelect===true
        typeInfo: {
            ids: [],
            labels: [],
            multiSelect: false,
        },
        editor: EnumTypeEditor,
        validation: selectTypeValidation,
    },
];

/* helper utils */
/**
 * List of user friendly labels for selector
 * @type {string[]}
 */
const typeLabelsList = typesData.map((typeData) => typeData.label);

/**
 *
 * @param {string} label
 * @return {CustomFieldTypeData|undefined}
 */
const typeDataByLabel = (label) => typesData.find((typeData) => typeData.label === label);

/**
 *
 * @param dataType
 * @return {string|undefined}
 */
const labelByTypeData = (dataType) => {
    const typeData = typesData.find((typeData) => typeData.dataType === dataType) || {};
    return typeData.label;
};

/**
 *
 * @param {string} dataType
 * @return {CustomFieldTypeData|undefined}
 */
const getTypeData = (dataType) => typeDataByLabel(labelByTypeData(dataType));

/*************************************************/
/* Utils to validate value according to typeInfo */
/*************************************************/

function stringTypeValidation(value, typeInfo = {}) {
    const {String: _String = {}} = typeInfo;
    let isValid = true;
    if (_String.pattern && value) {
        try {
            isValid = new RegExp(_String.pattern).test(value);
        } catch (e) {
            isValid = false;
        }
    }
    return {
        valid: isValid,
        msg: isValid ? null : "Value does not match string pattern",
    };
}
function selectTypeValidation(value, typeInfo) {
    const {ids} = typeInfo;
    if (value === null || value === "" || value === undefined) {
        return {
            valid: true,
            msg: null,
        };
    }
    const isValid = [].concat(value).every((id) => ids.includes(id));
    return {
        valid: isValid,
        msg: isValid ? null : "Wrong choice",
    };
}
function numberTypeValidation(value, typeInfo = {}) {
    const {Number: _Number = {}} = typeInfo;
    if (value === null) {
        return {
            valid: true,
            msg: null,
        };
    }

    if (value > _Number.max) {
        return {
            valid: false,
            msg: "Value must be less then max",
        };
    }

    if (value < _Number.min) {
        return {
            valid: false,
            msg: "Value must be greater then min",
        };
    }

    if (_Number.onlyInteger && !Number.isInteger(value)) {
        return {
            valid: false,
            msg: "Value must be integer",
        };
    }
}
function booleanTypeValidation(/*value, typeInfo*/) {
    return {
        valid: true,
        msg: null,
    };
}

module.exports = {
    typesData,
    typeLabelsList,
    typeDataByLabel,
    labelByTypeData,
    getTypeData,
};
